import { all, call, put, takeLatest } from "redux-saga/effects";
import LinkedinPromotionService from "../../services/linkedinPromotion.service";
import {
  getLinkedinInfoSuccess,
  getLinkedinInfoFailed,
  saveLinkedinInfoSuccess,
  saveLinkedinInfoFailed,
  getLinkedinDealsSuccess,
  getLinkedinDealsFailed,
  addLinkedinDealSuccess,
  addLinkedinDealFailed,
  updateLinkedinDealSuccess,
  updateLinkedinDealFailed,
  removeLinkedinDealSuccess,
  removeLinkedinDealFailed,
  getTotalLinkedinStatsSuccess,
  getTotalLinkedinStatsFailed,
  getTotalLinkedinStats,
} from "./linkedinPromotionSlice";
import { showErrorToaster, showSuccessToaster } from "../../helpers/utils/toaster";

function* linkedinPromotionWatcher() {
  yield takeLatest("linkedinPromotion/getLinkedinInfo", getLinkedinInfoSaga);
  yield takeLatest("linkedinPromotion/saveLinkedinInfo", saveLinkedinInfoSaga);
  yield takeLatest("linkedinPromotion/getLinkedinDeals", getLinkedinDealsSaga);
  yield takeLatest("linkedinPromotion/addLinkedinDeal", addLinkedinDealSaga);
  yield takeLatest("linkedinPromotion/updateLinkedinDeal", updateLinkedinDealSaga);
  yield takeLatest("linkedinPromotion/removeLinkedinDeal", removeLinkedinDealSaga);
  yield takeLatest("linkedinPromotion/recoverLinkedinDeal", recoverLinkedinDealSaga);
  yield takeLatest("linkedinPromotion/manualAdjustLinkedinDeal", manualAdjustLinkedinDealSaga);
  yield takeLatest("linkedinPromotion/getTotalLinkedinStats", getTotalLinkedinStatsSaga);
}

// ─── Info ────────────────────────────────────────────────────

function* getLinkedinInfoSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.getInfoByCompanyId, action.payload);
    if (response.status) {
      yield put(getLinkedinInfoSuccess(response.data));
    } else {
      yield put(getLinkedinInfoFailed(response.message));
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(getLinkedinInfoFailed("Failed to fetch LinkedIn info"));
  }
}

function* saveLinkedinInfoSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.saveInfo, action.payload);
    if (response.status) {
      yield put(saveLinkedinInfoSuccess(response.data));
      showSuccessToaster(response.message || "LinkedIn info saved successfully");
    } else {
      yield put(saveLinkedinInfoFailed());
      showErrorToaster(response.message);
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(saveLinkedinInfoFailed());
    showErrorToaster("Something went wrong");
  }
}

// ─── Deals ───────────────────────────────────────────────────

function* getLinkedinDealsSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.getDealsByCompanyId, action.payload);
    if (response.status) {
      yield put(getLinkedinDealsSuccess(response.data));
    } else {
      yield put(getLinkedinDealsFailed(response.message));
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(getLinkedinDealsFailed("Failed to fetch LinkedIn deals"));
  }
}

function* addLinkedinDealSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.addDeal, action.payload);
    if (response.status) {
      yield put(addLinkedinDealSuccess());
      yield put(getTotalLinkedinStats());
      showSuccessToaster(response.message || "Deal added successfully");
    } else {
      yield put(addLinkedinDealFailed());
      showErrorToaster(response.message);
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(addLinkedinDealFailed());
    showErrorToaster("Something went wrong");
  }
}

function* updateLinkedinDealSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.updateDeal, action.payload);
    if (response.status) {
      yield put(updateLinkedinDealSuccess());
      yield put(getTotalLinkedinStats());
      showSuccessToaster(response.message || "Deal updated successfully");
    } else {
      yield put(updateLinkedinDealFailed());
      showErrorToaster(response.message);
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(updateLinkedinDealFailed());
    showErrorToaster("Something went wrong");
  }
}

function* removeLinkedinDealSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.removeDeal, action.payload);
    if (response.status) {
      yield put(removeLinkedinDealSuccess());
      yield put(getTotalLinkedinStats());
      showSuccessToaster(response.message || "Deal removed successfully");
    } else {
      yield put(removeLinkedinDealFailed());
      showErrorToaster(response.message || "Failed to remove deal");
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(removeLinkedinDealFailed());
    showErrorToaster("Something went wrong");
  }
}

// ─── Existing ────────────────────────────────────────────────

function* recoverLinkedinDealSaga(action) {
  try {
    const response = yield call(LinkedinPromotionService.recoverDeal, action.payload.id);
    if (response.status) {
      yield put({ type: "linkedinPromotion/recoverLinkedinDealSuccess" });
      yield put(getTotalLinkedinStats());
      showSuccessToaster(response.message || "LinkedIn deal recovered successfully");
    } else {
      yield put({ type: "linkedinPromotion/recoverLinkedinDealFailed" });
      showErrorToaster(response.message || "Failed to recover LinkedIn deal");
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put({ type: "linkedinPromotion/recoverLinkedinDealFailed" });
    showErrorToaster("Something went wrong");
  }
}

function* manualAdjustLinkedinDealSaga(action) {
  try {
    const { companyId, amount } = action.payload;
    const response = yield call(LinkedinPromotionService.manualAdjustDeal, companyId, { amount });
    if (response.status) {
      yield put({ type: "linkedinPromotion/manualAdjustLinkedinDealSuccess" });
      yield put(getTotalLinkedinStats());
      showSuccessToaster(response.message || "Manual adjustment successful");
    } else {
      yield put({ type: "linkedinPromotion/manualAdjustLinkedinDealFailed" });
      showErrorToaster(response.message || "Failed to process manual adjustment");
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put({ type: "linkedinPromotion/manualAdjustLinkedinDealFailed" });
    showErrorToaster("Something went wrong");
  }
}

function* getTotalLinkedinStatsSaga() {
  try {
    const response = yield call(LinkedinPromotionService.getTotalStats);
    if (response.status) {
      yield put(getTotalLinkedinStatsSuccess(response.data));
    } else {
      yield put(getTotalLinkedinStatsFailed(response.message));
    }
  } catch (err) {
    console.log("Error: ", err);
    yield put(getTotalLinkedinStatsFailed("Failed to fetch LinkedIn promotion stats"));
  }
}

export default function* linkedinPromotionSaga() {
  yield all([linkedinPromotionWatcher()]);
}
